/**
 * Agregar al carrito desde la tarjeta de producto de KM (snippets/km-product-card y las tarjetas que arma el
 * buscador por moto). Ángel, 2026-10-15.
 *
 * Un solo listener para toda la página (delegación): cualquier `button.km-card__add[data-variant-id]` suma una
 * unidad de esa variante. Usa el mismo camino que el formulario de producto de Horizon:
 *   1. avisa con `CartLinesUpdateEvent` (de @shopify/events) y una promesa,
 *   2. POST a /cart/add.js pidiendo de vuelta las secciones del carrito abiertas en la página,
 *   3. resuelve la promesa con el carrito actualizado → el ícono del carrito y el cajón se actualizan solos.
 * El botón (círculo con ícono) pasa a «Agregando…» → «Agregado» (✓) o al error de Shopify (rojo, p. ej. sin stock)
 * y vuelve a su estado. El texto va oculto para lectores de pantalla y como `title`.
 *
 * Ficha de producto (sections/km-product): el botón lleva `data-km-add`, y además
 *   - `data-qty-source`: selector del campo de cantidad (si no hay, suma 1),
 *   - `data-msg-target`: selector donde se escribe el error de Shopify a la vista (ej. «solo quedan 2»).
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const RESET_MS = 1800;

const setState = (button, state, text) => {
  button.dataset.state = state;
  const value = text ?? button.dataset[`label${state[0].toUpperCase()}${state.slice(1)}`] ?? '';
  const label = button.querySelector('.km-card__add-text');
  if (label) label.textContent = value;
  button.title = value; // el botón es solo ícono: el estado (y el error de Shopify) se ve al pasar el mouse
};

const fetchCart = async () => {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems && typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
  const res = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.json`, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`cart ${res.status}`);
  return res.json();
};

const addToCart = async (button) => {
  const variantId = button.dataset.variantId;
  if (!variantId || button.dataset.state === 'loading') return;

  const qtyInput = button.dataset.qtySource && document.querySelector(button.dataset.qtySource);
  const quantity = Math.max(1, Math.floor(Number(qtyInput?.value) || 1));
  const msg = button.dataset.msgTarget && document.querySelector(button.dataset.msgTarget);
  if (msg) msg.textContent = '';

  setState(button, 'loading');
  const sections = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean)
    .join(',');

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity }], sections }),
    });
    const data = await res.json();
    const failed = Boolean(data.status);

    if (failed) {
      button.dispatchEvent(
        new CartErrorEvent({ error: data.message || 'No se pudo agregar', code: 'INVALID', detail: { description: data.description } })
      );
    }

    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'km-quick-add',
        itemCount: quantity,
        productId: button.dataset.productId,
        sections: data.sections,
        didError: failed,
      },
    });

    setState(button, failed ? 'error' : 'added', failed ? data.description || data.message : undefined);
    if (msg && failed) msg.textContent = data.description || data.message || 'No se pudo agregar';
  } catch (error) {
    deferred.reject(error);
    setState(button, 'error', 'Probá de nuevo');
    if (msg) msg.textContent = 'No se pudo agregar. Probá de nuevo.';
  }

  setTimeout(() => setState(button, 'idle'), RESET_MS);
};

if (!window.kmQuickAddReady) {
  window.kmQuickAddReady = true;
  document.addEventListener('click', (event) => {
    const button = event.target.closest('button.km-card__add[data-variant-id], button[data-km-add][data-variant-id]');
    if (!button) return;
    event.preventDefault();
    addToCart(button);
  });
}
